import { Col, ConfigProvider, Form, Input, message, Row, Button, Checkbox, Select } from "antd";
import styles from "./index.module.scss";
import authStore from "../../stores/authStore";
import { observer } from "mobx-react";
import classNames from "classnames";
import { useLocation, useNavigate } from "react-router";
import { useWindowSize } from "@uidotdev/usehooks";
import { useEffect, useState } from "react";
import AppConsts from "../../libs/AppConsts";
import DocumentModal from "../../components/DocumentsModal";

interface PromoObject {
  code: string;
  name?: string;
  id?: string;
  status?: "success" | "error";
}

const AuthView = observer(() => {
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [form] = Form.useForm();
  const [agreedToTerms, setAgreedToTerms] = useState(true);
  const location = useLocation();
  const size = useWindowSize();
  const [promo, setPromo] = useState<PromoObject>({ code: "" });

  const queryParameters = new URLSearchParams(location.search);
  const invitation = queryParameters.get("invitation");
  const state = queryParameters.get("state");
  const email = queryParameters.get("email");
  const token = queryParameters.get("token");
  const promoPeriodRenewToken = queryParameters.get("promo_period_renew_token");
  const nav = useNavigate();
  const isMobile = size.width && size.width < 600;

  useEffect(() => {
    if (invitation) {
      nav(`/register/${invitation}`, { replace: true });
    }
    if (state === "register") {
      onRegisterState();
    }
    if (state === "forgot") {
      onForgotState();
    }
    if (state === "reset") {
      onResetState();
    }
    authStore.fetchCountries();
  }, [invitation]);

  useEffect(() => {
    if (selectedCountry) {
      authStore.fetchCities(selectedCountry);
    }
  }, [selectedCountry]);

  useEffect(() => {
    if (!authStore.isRegister) {
      setAgreedToTerms(true);
    }
  }, [authStore.isRegister]);

  const onConfirm = async () => {
    if (authStore.isLoading || (authStore.isRegister && !agreedToTerms)) {
      return;
    }
    try {
      await form.validateFields();
      const values = form.getFieldsValue();
      if (authStore.isLogin) {
        let dto = values;
        if (promoPeriodRenewToken) {
          dto = { ...values, promoPeriodRenewToken: promoPeriodRenewToken };
        }
        await authStore.login(dto);
        form.resetFields();
      }
      if (authStore.isRegister) {
        let dto = values;
        if (promo.status === "success") {
          dto = { ...values, promocodes: [promo.id] };
        }
        const domain = authStore.lookups?.domains.find((e) => e.value === "ceilings");
        const loginCredentials = await authStore.register({ ...dto, domainIds: [domain?.id] });
        await authStore.login(loginCredentials);
      }
      if (authStore.isForgot) {
        await authStore.forgotPassword(values);
        form.resetFields();
      }
      if (authStore.isReset && token) {
        const response = await authStore.resetPassword({
          email: values.email,
          password: values.password,
          password_confirmation: values.passwordConfirmation,
          token,
        });
        form.resetFields();
        message.success(response?.message);
        onLoginState();
      }
    } catch (error: any) {
      console.error("Error in onConfirm:", error);

      if (error.errorFields) {
        form.setFields(error.errorFields);
        return;
      }

      if (error.response?.data?.messages) {
        const errors = Object.keys(error.response.data.messages).map((field) => ({
          name: field,
          errors: error.response.data.messages[field],
        }));
        form.setFields(errors);
      } else if (error.response?.data?.message) {
        form.setFields([
          {
            name: "password",
            errors: [error.response.data.message],
          },
        ]);
      } else if (error.message) {
        message.error(error.message);
      } else {
        message.error("Произошла ошибка. Пожалуйста, попробуйте снова.");
      }
    }
  };

  const onCheckPromoCode = async () => {
    if (promo.code === "") return;
    if (authStore.isLoading) return;
    const result = await authStore.checkPromoCode(promo.code);
    if (result?.status === "success") {
      setPromo({
        code: "",
        name: result.data.code,
        id: result.data.id,
        status: "success",
      });
    } else {
      setPromo({ ...promo, status: "error" });
    }
  };

  const onRegisterState = async () => {
    nav("?state=register");
    authStore.entry = "Register";
    await authStore.onFetchLookups();
    form.resetFields();
    setPromo({ code: "" });
  };

  const onForgotState = async () => {
    if (state !== "forgot") {
      nav("?state=forgot");
    }
    authStore.entry = "Forgot";
    form.resetFields();
  };

  const onResetState = async () => {
    if (state !== "reset") {
      nav("?state=reset");
    }
    authStore.entry = "Reset";
    form.resetFields();
    if (email && token) {
      form.setFieldValue("email", email);
    } else {
      message.error("Неверная ссылка", 100);
      onLoginState();
    }
  };

  const onLoginState = () => {
    nav("/", { replace: true });
    form.resetFields();
    authStore.entry = "Password";
  };

  const handleCountryChange = (value: string) => {
    setSelectedCountry(value);
    authStore.fetchCities(value);
    form.setFieldValue("cityId", undefined);
  };

   return (
    <ConfigProvider
      theme={{
        components: {
          Form: {
            itemMarginBottom: 12,
          },
          Input: {
            controlHeight: state === "register" ? 37 : 40,
          },
          InputNumber: {
            controlHeight: state === "register" ? 37 : 40,
          },
          Select: {
            controlHeight: state === "register" ? 37 : 40,
          },
        },
      }}
    >
      <div className={classNames(styles.area, "fade-in-opacity")}>
        <div className={styles.cover}>
          <div className={styles.form} style={{ width: state === "register" ? 490 : "auto" }}>
            <Form layout="vertical" form={form}>
              <div className={styles.logo} />
              <h2>Добро пожаловать в Flite CRM</h2>
              {authStore.isLogin && <p>Пожалуйста войдите в свою учетную запись</p>}
              {authStore.isRegister && <p>Создайте свою новую учетную запись</p>}
              {authStore.isForgot && <p>Пожалуйста введите свой Email</p>}
              {authStore.isReset && <p>Пожалуйста введите новый пароль</p>}              
              {authStore.isRegister && (
                // <Row gutter={12}>
                //   <Col span={12}>
                //     <Form.Item name="firstName" rules={[{ required: true, message: "" }]}>
                //       <Input placeholder="Имя*" />
                //     </Form.Item>
                //   </Col>
                //   <Col span={isMobile ? 24 : 12}>
                //     <Form.Item name="lastName">
                //       <Input placeholder="Фамилия" />
                //     </Form.Item>
                //   </Col>
                // </Row>
                <Form.Item name="firstName" rules={[{ required: true, message: "" }]}>
                  <Input placeholder="Имя*" />
                </Form.Item>
              )}

              {authStore.isRegister && (
                <Row gutter={12}>
                  <Col span={isMobile ? 24 : 12}>
                    <Form.Item name="countryId">
                      <Select
                        placeholder="Выберите страну"
                        onChange={handleCountryChange}
                        options={authStore.countries?.map((country) => ({
                          label: country.name,
                          value: country.id,
                        }))}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={isMobile ? 24 : 12}>
                    <Form.Item name="cityId">
                      <Select
                        showSearch
                        placeholder="Выберите город"
                        optionFilterProp="label"
                        // filterOption={(input, option) => option?.label.toLowerCase().includes(input.toLowerCase())}
                        optionLabelProp="customLabel"
                      >
                        {authStore.cities?.map((city) => (
                          <Select.Option
                            key={city.id}
                            value={city.id}
                            label={city.name}
                            customLabel={<span style={{ fontWeight: 500 }}>{city.name}</span>}
                          >
                            <div>
                              <div>{city.name}</div>
                              {city.district && (
                                <div
                                  style={{
                                    fontSize: "12px",
                                    color: "#888",
                                    lineHeight: "1.2",
                                  }}
                                >
                                  {city.district}
                                </div>
                              )}
                            </div>
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
              )}

              {authStore.isRegister && (
                <Row gutter={12}>
                  {/* <Col span={isMobile ? 24 : 12}>
                    <Form.Item name="domainIds" rules={[{ required: true, message: "" }]}>
                      <Select mode="multiple" placeholder="Деятельность">
                        {authStore.lookups?.domains.map((e) => (
                          <Select.Option key={e.id} value={e.id}>
                            {e.title}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col> */}
                  <Col span={24}>
                    <Form.Item name="phone" rules={[{ required: true, message: "" }]}>
                      <Input placeholder="Номер телефона*" type="tel" />
                    </Form.Item>
                  </Col>
                </Row>
              )}

              <Form.Item name="email" rules={[{ required: true, message: "" }]}>
                <Input disabled={authStore.isReset} placeholder={authStore.isRegister ? "Email*" : "Email"} />
              </Form.Item>

              {!authStore.isForgot && (
                <>
                  <Form.Item
                    name="password"
                    rules={[{ required: true, message: "" }]}
                    style={authStore.isLogin ? { marginBottom: 8 } : undefined}
                  >
                    <Input.Password placeholder={authStore.isRegister ? "Придумайте пароль для входа*" : "Пароль"} />
                  </Form.Item>
                  {authStore.isRegister && (
                    <div style={{ fontSize: 12, color: "#888", margin: 8 }}>Пароль должен содержать минимум 8 символов</div>
                  )}
                  {authStore.isLogin && (
                    <div style={{ textAlign: "right", marginBottom: 8 }}>
                      <Button type="link" htmlType="button" className={styles.forgotPassword} onClick={onForgotState}>
                        Забыли пароль?
                      </Button>
                    </div>
                  )}
                </>
              )}

              {(authStore.isRegister || authStore.isReset) && (
                <Form.Item name="passwordConfirmation" rules={[{ required: true, message: "" }]}>
                  <Input.Password placeholder="Повторите пароль*" />
                </Form.Item>
              )}

              {authStore.isRegister && (
                <>
                  <Form.Item name="coupon">
                    <Input disabled={authStore.isReset} placeholder="Введите купон" />
                  </Form.Item>
                  {/* <Form.Item>
                    <Flex gap={12}>
                      <Input
                        placeholder="Укажите промокод"
                        value={promo.code}
                        onChange={(e) => setPromo({ ...promo, code: e.target.value })}
                      />
                      <div className={styles.applyPromo} onClick={onCheckPromoCode}>
                        <ArrowRightShort />
                      </div>
                    </Flex>
                    {promo.status === "success" && (
                      <div className={styles.addedPromo}>
                        {promo.name}{" "}
                        <XLg
                          cursor={"pointer"}
                          onClick={() => {
                            setPromo({ code: "" });
                          }}
                        />
                      </div>
                    )}
                    {promo.status === "error" && (
                      <div className={styles.promoError}>
                        <InfoCircle color="red" />
                        {"Промокод недействителен"}
                      </div>
                    )}
                  </Form.Item> */}
                </>
              )}

              {authStore.isRegister && (
                <Form.Item>
                  <Checkbox 
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    className={styles.termsCheckbox}
                  >
                    <div className={styles.terms}>
                      Нажимая на кнопку "Подтвердить", вы даёте согласие на обработку своих персональных данных согласно{" "}
                      <DocumentModal
                        documentUrl={`${AppConsts.host}system-files/Политика_конфиденциальности_РФ_Flite_CRM_2025.docx`}
                        documentTitle="Политика конфиденциальности"
                        modalTitle="Политика конфиденциальности"
                      >
                        ПОЛИТИКЕ КОНФИДЕНЦИАЛЬНОСТИ
                      </DocumentModal>{" "}
                      и соглашаетесь с{" "}
                      <DocumentModal
                        documentUrl={`${AppConsts.host}system-files/Лицензионный_договор_оферта_РФ_Flite_CRM_2025.docx`}
                        documentTitle="Лицензионный договор оферта"
                        modalTitle="Лицензионный договор оферта"
                      >
                        ОФЕРТОЙ
                      </DocumentModal>
                      .
                    </div>
                  </Checkbox>
                </Form.Item>
              )}

              {authStore.isRegister && (
                <div 
                  onClick={onConfirm} 
                  className={classNames(styles.button, { 
                    [styles.buttonDisabled]: !agreedToTerms 
                  })}
                  style={!agreedToTerms ? { 
                    cursor: 'not-allowed', 
                    opacity: 0.6 
                  } : {}}
                >
                  {authStore.isLoading ? "Пожалуйста подождите" : "Подтвердить"}
                </div>
              )}

              {authStore.isLogin && (
                <div onClick={onConfirm} className={styles.button}>
                  {authStore.isLoading ? "Пожалуйста подождите" : "Войти"}
                </div>
              )}

              {authStore.isForgot && (
                <div onClick={onConfirm} className={styles.button}>
                  {authStore.isLoading ? "Пожалуйста подождите" : "Восстановить"}
                </div>
              )}

              {authStore.isReset && (
                <div onClick={onConfirm} className={styles.button}>
                  {authStore.isLoading ? "Пожалуйста подождите" : "Изменить"}
                </div>
              )}

              {(authStore.isRegister || authStore.isForgot) && (
                <div className={styles.createAccount} onClick={onLoginState}>
                  Войти в учетную запись
                </div>
              )}
              {authStore.isLogin && (
                <div className={styles.createAccount} onClick={onRegisterState}>
                  Зарегистрироваться
                </div>
              )}
            </Form>
          </div>
        </div>
      </div>
    </ConfigProvider>
  );
});

export default AuthView;
